import { SaveRounded } from "@mui/icons-material";
import { Box, Divider, Grid, Paper, Typography } from "@mui/material";
import { useFormik } from "formik";
import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import {
  AppDispatch,
  RootState,
  useAppDispatch,
} from "../../../../redux/store";
import * as UserActions from "../../../../redux/users/users.actions";
import * as userReducer from "../../../../redux/users/users.reducers";
import Button3D from "../../../../utils/common/3d-button/Button3D";
import MyTextField from "../../../../utils/common/my-textfield/MyTextField";
import LayoutHeading from "../../../layout/components/layout-heading/LayoutHeading";
import MainNavBar from "../../../layout/pages/navbar/MainNavBar";
import { primary } from "../../../../themes/theme";

const validationSchema = Yup.object().shape({
  password: Yup.string()
    .trim()
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*[0-9])(?=.*[!@#$%^&*()\-=_+{}\[\]:;"'<>,.?/~`\\|]).{6,15}$/,
      "Password must be between 6 and 15 characters, and include at least one uppercase letter, one lowercase letter, one number, and one special character"
    )
    .required("Password is required"),
  confirmPassword: Yup.string()
    .trim()
    .oneOf([Yup.ref("password"), undefined], "Passwords must match")
    .required("Confirm Password is required"),
});

const PasswordManagement: React.FC = () => {
  const dispatch: AppDispatch = useAppDispatch();
  const navigate = useNavigate();

  //Get data from redux store
  const userState: userReducer.InitialState = useSelector(
    (state: RootState) => {
      return state[userReducer.userFeatureKey];
    }
  );

  const { loading } = userState;

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (value) => {
      await handleUpdatePassword(value);
      // formik.resetForm();
    },
  });

  const handleUpdatePassword = async (value: any) => {
    const { password } = value;
    dispatch(UserActions.changePasswordAction({ password })).then(
      (response: any) => {
        if (response && !response.error) {
          navigate("/products/fashion");
        }
      }
    );
  };

  return (
    <>
      <Box sx={{ mb: 1, pb: 1 }}>
        <MainNavBar />
        <LayoutHeading
          heading="Password Management"
          description="Safely manage passwords, updates, and security settings with ease and precision."
        />
        <Grid container sx={{ px: { xs: 1, md: 9, lg: 10 } }}>
          <Grid
            item
            xs={12}
            sm={12}
            md={8}
            lg={7}
            xl={6}
            component={Paper}
            elevation={0}
            sx={{ borderRadius: "10px", py: 2, px: { xs: 0, sm: 1 } }}
          >
            <form onSubmit={formik.handleSubmit}>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Ubuntu",
                    fontSize: "17px",
                    letterSpacing: "1px",
                    pl: 3,
                    fontWeight: "550",
                  }}
                >
                  | Change Your Password
                </Typography>
              </Box>
              <Divider
                sx={{
                  mt: 1,
                  mb: 0,
                  color: primary[900],
                  border: "1px dashed #f1f2f3",
                }}
              />
              <MyTextField
                formik={formik}
                name="password"
                type="password"
                placeholder="New Password"
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={20}
                    height={20}
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="currentColor"
                      d="M12 17a2 2 0 0 0 2-2a2 2 0 0 0-2-2a2 2 0 0 0-2 2a2 2 0 0 0 2 2m6-9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h1V6a5 5 0 0 1 5-5a5 5 0 0 1 5 5v2zm-6-5a3 3 0 0 0-3 3v2h6V6a3 3 0 0 0-3-3"
                    ></path>
                  </svg>
                }
              />
              <MyTextField
                formik={formik}
                name="confirmPassword"
                type="text"
                placeholder="Confirm Password"
                icon={
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={20}
                    height={20}
                    viewBox="0 0 24 24"
                  >
                    <path
                      fill="currentColor"
                      d="M19 13c.34 0 .67.04 1 .09V10a2 2 0 0 0-2-2h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6a2 2 0 0 0-2 2v10c0 1.11.89 2 2 2h7.81c-.51-.88-.81-1.9-.81-3c0-3.31 2.69-6 6-6M9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9zm3 11a2 2 0 1 1 2-2c0 1.11-.89 2-2 2m10.5.25L17.75 22L15 19l1.16-1.16l1.59 1.59l3.59-3.59z"
                    ></path>
                  </svg>
                }
              />
              <Box sx={{ pl: { xs: 3, sm: 3 }, py: 1 }}>
                <Button3D
                  type="submit"
                  buttonText="Update"
                  buttonIcon={
                    <SaveRounded
                      fontSize="small"
                      sx={{ pr: "5px", pt: "2px" }}
                    />
                  }
                />
              </Box>
            </form>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default PasswordManagement;
